import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UploadSimple, ArrowsClockwise, PaperPlaneTilt, Eye, Trash, UsersThree, Ticket } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../../lib/api";

const SOURCE_LABEL = { import: "Imported", bookings: "Past bookings", packs: "Pack buyers", clients: "1:1 clients" };

export default function GroupMigrationSection() {
  const [contacts, setContacts] = useState(null);
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [testEmail, setTestEmail] = useState("");

  const load = async () => {
    const [c, d, h] = await Promise.all([
      api.get("/group/migration/contacts"), api.get("/group/migration/campaign/default"), api.get("/group/migration/campaigns"),
    ]);
    setContacts(c.data);
    setForm((f) => f || { subject: d.data.subject, headline: d.data.headline, message: d.data.message, cta_label: d.data.cta_label, include_sources: d.data.sources, cta_url: d.data.cta_url });
    setCampaigns(h.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);
  useEffect(() => {
    if (!campaigns.some((c) => c.status === "sending")) return;
    const t = setInterval(() => api.get("/group/migration/campaigns").then(({ data }) => setCampaigns(data)).catch(() => {}), 3000);
    return () => clearInterval(t);
  }, [campaigns]);

  const run = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); if (ok) toast.success(typeof ok === "function" ? ok(r) : ok); await load(); return r; }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const importRaw = () => run(() => api.post("/group/migration/contacts/import", { raw }).then((r) => { setRaw(""); return r; }), (r) => {
    const d = r.data;
    const credits = d.credits_granted || d.credits_adjusted ? ` · credits carried over for ${d.credits_granted + d.credits_adjusted}` : "";
    return `Imported ${d.added} new, ${d.updated} already on file${credits}`;
  });
  const sync = () => run(() => api.post("/group/migration/contacts/sync"), (r) => { const d = r.data; return `Pulled ${d.bookings.added + d.packs.added + d.clients.added} new contacts from your records`; });
  const remove = (c) => run(() => api.delete(`/group/migration/contacts/${c.id}`), "Removed");
  const doPreview = () => run(() => api.post("/group/migration/campaign/preview", form).then((r) => { setPreview(r.data); return r; }));
  const sendTest = () => run(() => api.post("/group/migration/campaign/send", { ...form, test_email: testEmail }), (r) => `Test sent to ${r.data.to}`);
  const sendAll = async () => {
    const n = preview?.recipients ?? contacts?.reachable ?? 0;
    if (!window.confirm(`Send this announcement to ${n} contact${n === 1 ? "" : "s"} now?`)) return;
    await run(() => api.post("/group/migration/campaign/send", { ...form, test_email: null }), (r) => `Sending to ${r.data.total} contacts…`);
  };
  const toggleSource = (s) => setForm({ ...form, include_sources: form.include_sources.includes(s) ? form.include_sources.filter((x) => x !== s) : [...form.include_sources, s] });

  if (!contacts || !form) return <div className="card-flat p-12 text-center tracker text-zinc-500">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="group-migration">
      <div className="card-flat p-6 border-brand/30">
        <div className="tracker text-brand mb-2">[ Moving your business onto Traina ]</div>
        <p className="text-sm text-zinc-300 max-w-2xl">Bring your existing customer list across and tell everyone where to book from now on. Your public schedule link (<a href={form.cta_url} target="_blank" rel="noopener noreferrer" className="text-brand-light underline" data-testid="migration-cta-url">{form.cta_url}</a>) goes in every email, branded as {" "}your business.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="card-flat p-6 space-y-4" data-testid="migration-contacts">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl">1 · Your contacts <span className="text-zinc-500 text-lg">({contacts.total})</span></h2>
            <button onClick={sync} disabled={busy} className="btn-ghost !py-1.5 !px-3 !text-[10px]" title="Pull everyone who has booked a class, bought a pack or is a 1:1 client" data-testid="migration-sync">
              <ArrowsClockwise size={12} /> Pull from my records
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(contacts.by_source).map(([s, n]) => (
              <span key={s} className="tracker text-[9px] px-2 py-1 border border-white/10 text-zinc-400" data-testid={`migration-source-${s}`}>{SOURCE_LABEL[s]} · {n}</span>
            ))}
            {contacts.credits_total > 0 && (
              <span className="tracker text-[9px] px-2 py-1 border border-emerald-500/40 text-emerald-400 inline-flex items-center gap-1" data-testid="migration-credits-total">
                <Ticket size={10} /> {contacts.credits_total} credits carried over · {contacts.with_credits} people
              </span>
            )}
          </div>
          <div className="border border-brand/30 bg-brand/5 px-4 py-3 text-xs text-zinc-300" data-testid="migration-credits-hint">
            <strong className="text-brand-light">Old credits roll over automatically.</strong> Include a credits / remaining column in your export (or write "5 credits" after an email) and each person's balance is loaded against their email — they just book as normal and it's used first. They get a branded email telling them.
          </div>
          <textarea value={raw} onChange={(e) => setRaw(e.target.value)} rows={5} placeholder={"Paste emails (one per line) or a CSV export from your old system with 'email' and 'credits' columns…\n\nname,email,phone,credits remaining\nJane Smith,jane@example.com,07700 900123,5\nsam@example.com 3 credits"} className="w-full px-4 py-3 border text-sm font-mono" data-testid="migration-raw" />
          <button onClick={importRaw} disabled={busy || !raw.trim()} className="btn-primary !text-xs disabled:opacity-40" data-testid="migration-import"><UploadSimple size={14} /> Import list</button>
          <div className="max-h-72 overflow-y-auto divide-y divide-white/5 border border-white/10" data-testid="migration-contact-list">
            {contacts.contacts.length === 0 ? (
              <p className="p-6 text-center text-zinc-500 text-sm">No contacts yet — paste a list or pull from your records.</p>
            ) : contacts.contacts.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-3 px-4 py-2" data-testid={`migration-contact-${c.id}`}>
                <div className="min-w-0">
                  <div className="text-sm truncate">{c.name || <span className="text-zinc-500">—</span>} <span className="font-mono text-[10px] text-zinc-500">{c.email}</span></div>
                  <div className="tracker text-[9px] text-zinc-600">{(c.sources || []).map((s) => SOURCE_LABEL[s]).join(" · ")}{c.last_notified_at ? " · notified" : ""}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {c.credits_left > 0 && (
                    <span className="tracker text-[9px] px-2 py-0.5 border border-emerald-500/40 text-emerald-400" data-testid={`migration-contact-credits-${c.id}`}>{c.credits_left} CREDITS</span>
                  )}
                  <button onClick={() => remove(c)} className="text-zinc-600 hover:text-red-400" data-testid={`migration-contact-remove-${c.id}`}><Trash size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="card-flat p-6 space-y-4" data-testid="migration-campaign">
          <h2 className="font-display text-2xl">2 · The announcement</h2>
          <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full px-4 py-3 border text-sm" placeholder="Email subject" data-testid="migration-subject" />
          <input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} className="w-full px-4 py-3 border text-sm" placeholder="Headline" data-testid="migration-headline" />
          <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={9} className="w-full px-4 py-3 border text-sm" data-testid="migration-message" />
          <input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} className="w-full px-4 py-3 border text-sm" placeholder="Button text" data-testid="migration-cta-label" />
          <div>
            <div className="tracker text-zinc-500 text-[10px] mb-2">SEND TO</div>
            <div className="flex flex-wrap gap-2">
              {Object.keys(SOURCE_LABEL).map((s) => (
                <button key={s} onClick={() => toggleSource(s)} className={`tracker text-[9px] px-2 py-1 border ${form.include_sources.includes(s) ? "border-brand text-white" : "border-white/10 text-zinc-500"}`} data-testid={`migration-include-${s}`}>{SOURCE_LABEL[s]}</button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <button onClick={doPreview} disabled={busy} className="btn-ghost !text-xs" data-testid="migration-preview"><Eye size={14} /> Preview</button>
            <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@…" className="px-3 py-2 border text-xs w-44" data-testid="migration-test-email" />
            <button onClick={sendTest} disabled={busy || !testEmail} className="btn-ghost !text-xs disabled:opacity-40" data-testid="migration-send-test">Send test</button>
            <button onClick={sendAll} disabled={busy || contacts.reachable === 0} className="btn-primary !text-xs ml-auto disabled:opacity-40" data-testid="migration-send-all">
              <PaperPlaneTilt size={14} /> Send to {preview?.recipients ?? contacts.reachable}
            </button>
          </div>
          {preview && (
            <div className="border border-white/10" data-testid="migration-preview-frame">
              <div className="tracker text-[9px] text-zinc-500 px-3 py-2 border-b border-white/10">PREVIEW · {preview.recipients} recipients</div>
              <iframe title="preview" srcDoc={preview.html} className="w-full h-[420px] bg-black" />
            </div>
          )}
        </section>
      </div>

      {campaigns.length > 0 && (
        <section className="card-flat p-6" data-testid="migration-history">
          <h2 className="font-display text-2xl mb-3 flex items-center gap-2"><UsersThree size={20} weight="duotone" className="text-brand-light" /> Sent announcements</h2>
          <div className="divide-y divide-white/5">
            {campaigns.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`migration-campaign-${c.id}`}>
                <div className="min-w-0"><div className="text-sm truncate">{c.subject}</div><div className="tracker text-[9px] text-zinc-500">{new Date(c.created_at).toLocaleString()}</div></div>
                <span className={`tracker text-[9px] px-2 py-1 border ${c.status === "done" ? "border-emerald-500/40 text-emerald-400" : "border-amber-500/40 text-amber-400"}`}>
                  {c.status === "done" ? `SENT ${c.sent}/${c.total}` : `SENDING ${c.sent}/${c.total}`}{c.failed ? ` · ${c.failed} failed` : ""}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
